/* eslint-disable promise/always-return */
/* eslint-disable promise/prefer-await-to-then */
/* eslint-disable @typescript-eslint/no-use-before-define */
/**
 * Worker Initialization Module
 * Handles initialization and management of all web workers
 */

import { ShopConfigDataHandler } from '@/core/data_handlers/shop_config_data';
import { Logger } from '@/shared/logger/logger';

import { WorkerManager } from './worker_manager';

// Global worker managers
let ewsWorkerManager: WorkerManager | null = null;
let eventsWorkerManager: WorkerManager | null = null;

/**
 * Initialize all workers
 * @returns True if initialization was successful
 */
export function initWorkers(): boolean {
  const shopConfig = ShopConfigDataHandler.getCurrentConfig();
  const workerRetryEnabled =
    shopConfig.config.script_tag_worker_retry_enabled ?? false;

  try {
    Logger.info('[Workers] Initializing workers...', {
      retryEnabled: workerRetryEnabled,
    });

    // Get the main thread's bundle name to pass to workers
    const mainThreadBundleName = Logger.bundleName;

    // Initialize Events worker with bundle name
    eventsWorkerManager = new WorkerManager('events-worker.js', {
      bundleName: 'events',
      mainThreadBundleName,
      retryEnabled: workerRetryEnabled,
    });

    // Initialize EWS worker with just the filename and bundle name
    // The WorkerManager will now handle the full URL construction using STATIC_API_BASE_URL
    ewsWorkerManager = new WorkerManager('ews-worker.js', {
      bundleName: 'ews',
      mainThreadBundleName,
      retryEnabled: workerRetryEnabled,
    });

    // Test Events worker connectivity
    testWorkerConnectivity(eventsWorkerManager, 'Events');

    // Test EWS worker connectivity
    testWorkerConnectivity(ewsWorkerManager, 'EWS');

    return true;
  } catch (error) {
    Logger.error('[Workers] Failed to initialize workers', error);
    return false;
  }
}

/**
 * Test worker connectivity
 * @param workerManager - The worker manager to test
 * @param workerName - Name of the worker for logging
 */
function testWorkerConnectivity(
  workerManager: WorkerManager | null,
  workerName: string,
): void {
  if (!workerManager) {
    Logger.error(`[Workers] ${workerName} worker manager is null`);
    return;
  }

  workerManager
    .executeTask('TEST', {
      message: `${workerName} worker initialization test`,
    })
    .then(() => {
      Logger.info(`[Workers] ${workerName} worker initialized successfully`);
    })
    .catch(error => {
      // Extract any worker details from the error if available
      let errorDetails = '';
      let workerUrl = '';

      if (error?.message) {
        errorDetails = error.message;

        // Try to extract URL from error message if it exists
        const urlMatch = error.message.match(/https?:\/\/[^\s")]+/);
        if (urlMatch) {
          workerUrl = urlMatch[0];
        }
      }

      // Check if we have any additional worker details from the WorkerManager
      const additionalInfo = error?.workerDetails || {};

      Logger.error(
        `[Workers] ${workerName} worker initialization failed`,
        error,
        {
          errorDetails: errorDetails,
          extractedUrl: workerUrl,
          workerInfo: additionalInfo,
          workerName,
        },
      );
    });
}

/**
 * Get the EWS worker manager
 * @returns The EWS worker manager or null if not initialized
 */
export function getEwsWorker(): WorkerManager | null {
  if (ewsWorkerManager) {
    Logger.debug(
      `[Workers] Returning EWS worker with origin: ${(ewsWorkerManager as any).origin}`,
    );
  } else {
    Logger.debug('[Workers] EWS worker not initialized yet when requested');
  }
  return ewsWorkerManager;
}

/**
 * Get the Events worker manager
 * @returns The Events worker manager or null if not initialized
 */
export function getEventsWorker(): WorkerManager | null {
  if (eventsWorkerManager) {
    Logger.debug(
      `[Workers] Returning Events worker with origin: ${(eventsWorkerManager as any).origin}`,
    );
  } else {
    Logger.debug('[Workers] Events worker not initialized yet when requested');
  }
  return eventsWorkerManager;
}
